import { useEffect, useRef } from 'react';
import type { Command } from '@fabricos/protocol-ts';
import { sendCommand } from './useHubSocket';
import { useManualSession } from '@/store/manual';
import { useMapMode } from '@/store/mapMode';
import { useSelection } from '@/store/selection';

/**
 * Manual control — thin client. Reads keyboard / gamepad sticks and pushes
 * them at ~5 Hz to the Hub's `Command::ManualControl`. The Hub owns the
 * per-tick translation to body-frame velocity setpoints, the 10 Hz keepalive
 * stream, the GUIDED-mode arming, and the watchdog that lands the drone if
 * input goes stale (see `crates/hub-core/src/manual_session.rs`).
 *
 * Why thin: a browser tab can crash, freeze, blur, or hit a network hiccup.
 * If the keepalive lived here, the drone would track a stale setpoint until
 * the firmware's own 3-second GUIDED LAND failsafe tripped. The Hub's
 * session watchdog (`STICK_STALE_MS = 2 s`) catches all those failure modes
 * with a clean LOITER instead.
 *
 * Dead-man: hold Space to engage. Releasing Space sends ONE explicit
 * `set_mode brake`, which is the Hub's "operator-requested stop" trigger.
 * BRAKE stops the vehicle in place and uses the firmware-controlled
 * throttle to hold altitude — no RC stick required, safe in SITL and
 * production on ArduCopter (mode 17). The Hub's mode map falls BRAKE
 * back to PX4 AUTO_LOITER (GPS-based hold, also no RC dependency).
 *
 * Why BRAKE and not LOITER: ArduCopter LOITER expects a mid-stick RC
 * throttle to maintain altitude. In SITL without RC the implicit zero
 * reads as full descent and the drone hits the ground at ~2.5 m/s
 * (see DEBUG_HISTORY round 3 + the 2026-06-25 incident).
 */

/** Coarse send rate. We don't need 10 Hz from the client — the Hub does the
 *  10 Hz body-frame stream itself. We just need to refresh the operator's
 *  intent often enough that the Hub's stale-input watchdog doesn't trip. */
const SEND_HZ = 5;
const SEND_PERIOD_MS = Math.round(1000 / SEND_HZ);

interface Sticks {
  roll: number;
  pitch: number;
  yaw: number;
  throttle: number;
}

function zero(): Sticks {
  return { roll: 0, pitch: 0, yaw: 0, throttle: 0 };
}

function makeId(): string {
  return `mc-${Math.random().toString(36).slice(2, 8)}-${Date.now()}`;
}

export function useManualControl() {
  const sticks = useRef<Sticks>(zero());
  const engaged = useRef(false);
  const keys = useRef<Set<string>>(new Set());
  const interval = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || 'ontouchstart' in window) return;

    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (useMapMode.getState().mode !== 'manual') return;
      keys.current.add(e.code);
      if (e.code === 'Space' && !engaged.current) {
        engage();
        e.preventDefault();
      }
      updateFromKeys();
    }
    function onKeyUp(e: KeyboardEvent) {
      keys.current.delete(e.code);
      if (e.code === 'Space' && engaged.current) {
        disengage();
      }
      updateFromKeys();
    }
    function onBlur() {
      if (engaged.current) disengage();
      keys.current.clear();
    }
    function onVisibility() {
      if (document.hidden && engaged.current) disengage();
    }

    function updateFromKeys() {
      // Translation (body frame) on the arrow keys, altitude + yaw on WASD.
      // Mapping aligns with Bramer / MAVSDK offboard semantics:
      //   pitch +1 → vx +3 m/s (forward in body NED)
      //   roll  +1 → vy +3 m/s (right)
      //   throttle +1 → vz -2 m/s (NED-down is +, so -vz = climb)
      //   yaw   +1 → yaw_rate +60 deg/s (clockwise, looking down)
      const k = keys.current;
      const s = zero();
      if (k.has('ArrowUp')) s.pitch = +1;
      if (k.has('ArrowDown')) s.pitch = -1;
      if (k.has('ArrowLeft')) s.roll = -1;
      if (k.has('ArrowRight')) s.roll = +1;
      if (k.has('KeyW')) s.throttle = +1;
      if (k.has('KeyS')) s.throttle = -1;
      if (k.has('KeyA')) s.yaw = -1;
      if (k.has('KeyD')) s.yaw = +1;
      sticks.current = s;
      if (engaged.current) useManualSession.getState().setSticks(s);
    }

    function pollGamepad() {
      if (useMapMode.getState().mode !== 'manual') return;
      if (!navigator.getGamepads) return;
      const pads = navigator.getGamepads();
      for (const pad of pads) {
        if (!pad) continue;
        const axes = pad.axes;
        if (axes.length < 4) continue;
        const lx = filter(axes[0] ?? 0);
        const ly = filter(axes[1] ?? 0);
        const rx = filter(axes[2] ?? 0);
        const ry = filter(axes[3] ?? 0);
        if (Math.abs(lx) + Math.abs(ly) + Math.abs(rx) + Math.abs(ry) > 0.05) {
          if (!engaged.current) engage();
          sticks.current = { roll: lx, pitch: -ly, yaw: rx, throttle: -ry };
          useManualSession.getState().setSticks(sticks.current);
        }
      }
    }

    function engage() {
      engaged.current = true;
      useManualSession.getState().setEngaged(true);
      // First send arrives at the Hub before the interval fires, kicks off
      // the Hub-side ManualSession (set_mode GUIDED + streamer task).
      sendSticksToSelection(zero());
    }
    function disengage() {
      engaged.current = false;
      sticks.current = zero();
      useManualSession.getState().setEngaged(false);
      // The Hub interprets `set_mode brake` as "operator stopped manual"
      // — tears down the session and forwards BRAKE to the drone, which
      // stops it in place and holds altitude without needing an RC stick.
      // PX4 maps BRAKE → AUTO_LOITER (a GPS hold that also works without
      // RC). See top-of-file docstring for the LOITER history.
      sendBrakeToSelection();
    }

    interval.current = window.setInterval(() => {
      pollGamepad();
      if (!engaged.current) return;
      sendSticksToSelection(sticks.current);
    }, SEND_PERIOD_MS);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (interval.current) window.clearInterval(interval.current);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}

function filter(v: number): number {
  if (Math.abs(v) < 0.08) return 0;
  const sign = Math.sign(v);
  const m = (Math.abs(v) - 0.08) / 0.92;
  return sign * m * m;
}

function sendBrakeToSelection() {
  const selection = useSelection.getState().selected;
  for (const robotId of selection) {
    sendCommand({
      kind: 'set_mode',
      id: makeId(),
      robot_id: robotId,
      priority: 'normal',
      mode: 'brake',
    });
  }
}

function sendSticksToSelection(s: Sticks) {
  const selection = useSelection.getState().selected;
  for (const robotId of selection) {
    const cmd: Command = {
      kind: 'manual_control',
      id: makeId(),
      robot_id: robotId,
      priority: 'high',
      roll: s.roll,
      pitch: s.pitch,
      yaw: s.yaw,
      throttle: s.throttle,
    };
    sendCommand(cmd);
  }
}

